import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageSquare, HelpCircle, Building2, ArrowRight, ShieldAlert } from "lucide-react";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import MarketingHeader from "@/components/landing/MarketingHeader";
import ContactForm from "@/components/ContactForm";
import { useTheme } from "@/context/ThemeProvider";
import {
    BRAND, BRAND_RED, BRAND_10, paletteClasses,
} from "@/lib/landing-theme";
import StructuredData from "@/components/seo/StructuredData";
import { CONTACT_PAGE_JSON_LD, buildBreadcrumbsJsonLd } from "@/components/seo/schema";
import { CrimsonCard } from "@/components/ui/system-primitives";

const CONTACT_EMAIL = "info@meliandialogue.com";
const CONTACT_PHONE_INTL = "+254758004013";
const CONTACT_PHONE_DISPLAY = "+254 758 004 013";
const WHATSAPP_URL = `https://wa.me/${CONTACT_PHONE_INTL.replace("+", "")}`;

const CHANNELS = [
    { icon: Mail,          label: "Email",     value: CONTACT_EMAIL,          href: `mailto:${CONTACT_EMAIL}`,
      hint: "Fastest for account, subscription, and institutional-onboarding queries. Response within 24h." },
    { icon: Phone,         label: "Phone",     value: CONTACT_PHONE_DISPLAY,  href: `tel:${CONTACT_PHONE_INTL}`,
      hint: "Best-effort · voicemail after hours. Escalations only." },
    { icon: MessageSquare, label: "WhatsApp",  value: CONTACT_PHONE_DISPLAY,  href: WHATSAPP_URL,
      hint: "Chat with the operator team. Voice notes welcome." },
];

export default function Contact() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const p = paletteClasses(isDark);

    return (
        <div className={`min-h-screen font-sans transition-colors duration-300 ${p.shell}`}>
            {/* SEO · ContactPage rich result. */}
            <StructuredData id="contact-page" json={CONTACT_PAGE_JSON_LD} />
            <StructuredData id="contact-crumbs" json={buildBreadcrumbsJsonLd([{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }])} />
            <MarketingHeader testidPrefix="contact" />

            <section className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-8 md:pb-12">
                <div className="overline mb-3" style={{ color: BRAND_RED }} data-testid="contact-eyebrow">
                    Get in touch
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1] mb-4"
                    data-testid="contact-h1">
                    We reply to <span style={{ color: BRAND }}>every message</span>. Usually within a day.
                </h1>
                <p className={`text-base md:text-lg leading-relaxed max-w-2xl ${p.subtleText}`}>
                    Questions about copy-trading rules, institutional subscriptions, or the CMA framework?
                    Direct message our operator team on any of the channels below. For sensitive account
                    issues, prefer email so we can verify identity properly.
                </p>

                {/* Crimson callout — urgent security / press escalations. */}
                <CrimsonCard testid="contact-urgent-callout" className="mt-6">
                    <div className="flex items-start gap-3">
                        <ShieldAlert width={20} height={20} strokeWidth={1.75} style={{ color: BRAND_RED }} />
                        <div>
                            <div className="font-heading font-semibold text-h3 mb-1" style={{ color: BRAND_RED }}>
                                Urgent security or press?
                            </div>
                            <p className="font-body text-body-base">
                                Suspected account compromise, media requests under embargo, or CMA / regulator
                                correspondence &mdash; email{" "}
                                <a href={`mailto:${CONTACT_EMAIL}`} className="font-heading font-semibold underline underline-offset-2" style={{ color: BRAND_RED }}>
                                    {CONTACT_EMAIL}
                                </a>{" "}
                                with <span className="font-heading font-medium">URGENT</span> in the subject
                                line. On-call operator response within 2 hours.
                            </p>
                        </div>
                    </div>
                </CrimsonCard>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-8 md:py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="contact-channels">
                    {CHANNELS.map((ch, i) => (
                        <a key={ch.label}
                           href={ch.href}
                           target={ch.href.startsWith("http") ? "_blank" : undefined}
                           rel={ch.href.startsWith("http") ? "noopener noreferrer" : undefined}
                           className={`block rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${p.cardBg} ${p.cardHoverBorder}`}
                           data-testid={`contact-channel-${ch.label.toLowerCase()}`}>
                            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                                 style={{ background: BRAND_10, color: BRAND }}>
                                <ch.icon className="w-5 h-5" strokeWidth={2.25} />
                            </div>
                            <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${p.faintText}`}>
                                {ch.label}
                            </div>
                            <div className="text-base font-extrabold mb-2 tracking-tight break-all"
                                 data-testid={`contact-value-${ch.label.toLowerCase()}`}>
                                {ch.value}
                            </div>
                            <p className={`text-sm leading-relaxed ${p.subtleText}`}>{ch.hint}</p>
                        </a>
                    ))}
                </div>
            </section>

            {/* Deflection strip — cuts inbound support volume by pointing common
                queries to the FAQ, and gives institutional visitors a direct
                path to the /apply Obondo API application form. */}
            <section className="max-w-4xl mx-auto px-6 py-2 md:py-4" data-testid="contact-deflection">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Link to="/faq"
                          data-testid="contact-deflect-faq"
                          className={`flex items-center gap-4 rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${p.cardBg} ${p.cardHoverBorder}`}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                             style={{ background: BRAND_10, color: BRAND }}>
                            <HelpCircle className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className={`text-[10px] uppercase tracking-widest font-bold ${p.faintText}`}>
                                Before you write
                            </div>
                            <div className="font-extrabold text-sm tracking-tight truncate">
                                Check the FAQ first — 80% of questions answered
                            </div>
                        </div>
                        <ArrowRight className="w-4 h-4 shrink-0" style={{ color: BRAND }} />
                    </Link>

                    <Link to="/apply"
                          data-testid="contact-deflect-institutional"
                          className={`flex items-center gap-4 rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${p.cardBg} ${p.cardHoverBorder}`}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                             style={{ background: BRAND_10, color: BRAND }}>
                            <Building2 className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className={`text-[10px] uppercase tracking-widest font-bold ${p.faintText}`}>
                                Institutional · Obondo API
                            </div>
                            <div className="font-extrabold text-sm tracking-tight truncate">
                                Apply for API access — schools, brokers & fintechs →
                            </div>
                        </div>
                        <ArrowRight className="w-4 h-4 shrink-0" style={{ color: BRAND }} />
                    </Link>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-8 md:py-12">
                <div className={`rounded-2xl border p-6 md:p-8 ${p.cardBg}`} data-testid="contact-hq">
                    <div className="overline mb-3" style={{ color: BRAND_RED }}>Registered office</div>
                    <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 mt-0.5" style={{ color: BRAND }} />
                        <div>
                            <div className="font-extrabold text-lg tracking-tight mb-1">Melian Dialogue Limited</div>
                            <p className={`text-sm leading-relaxed ${p.subtleText}`}>
                                P.O. Box B1003, Urban Oasis Apartments,<br />
                                Kingara Road, Lavington,<br />
                                Nairobi, Republic of Kenya.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-8 md:py-12" data-testid="contact-form-section">
                <ContactForm
                    subtleText={p.subtleText}
                    faintText={p.faintText}
                    cardBg={p.cardBg}
                />
            </section>

            <ComplianceFooter />
        </div>
    );
}
